import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  Handshake,
  TrendingUp,
  ArrowUpRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function AdminDashboard() {
  const { admin } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin-portal/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000 // 30 second timeout
      });
      setDashboard(response.data);
    } catch (error) {
      // Only show error if it's not a timeout - data may still load
      if (error.code === 'ECONNABORTED') {
        console.log('Dashboard request timed out, retrying...');
        // Retry once with longer timeout
        try {
          const token = localStorage.getItem('admin_token');
          const retryResponse = await axios.get(`${API_URL}/api/admin-portal/dashboard`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000 // 60 second timeout for retry
          });
          setDashboard(retryResponse.data);
          return;
        } catch (retryError) {
          toast.error('Dashboard is taking longer than expected. Please refresh.');
        }
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">
          Platform overview and management
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">
              Total Companies
            </CardTitle>
            <Building2 className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{dashboard?.summary?.total_companies || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Registered companies</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">
              Total Users
            </CardTitle>
            <Users className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{dashboard?.summary?.total_users || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Across all companies</p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-400">
              Partners
            </CardTitle>
            <Handshake className="w-4 h-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{dashboard?.summary?.total_partners || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Registered partners</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-green-400">
              Active Subscriptions
            </CardTitle>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-400">{dashboard?.summary?.active_subscriptions || 0}</div>
            <p className="text-xs text-green-400/70 mt-1">Paying customers</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Companies */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Recent Companies</CardTitle>
              <CardDescription className="text-slate-400">Latest registered companies</CardDescription>
            </div>
            <Link to="/admin/companies">
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                View All
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_companies?.length > 0 ? (
              <div className="space-y-4">
                {dashboard.recent_companies.map((company) => (
                  <div 
                    key={company.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{company.name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <Users className="w-3 h-3" />
                          {company.user_count} users
                          <span className="mx-1">•</span>
                          <Badge variant="outline" className="text-xs capitalize border-slate-600 text-slate-300">
                            {company.plan || 'free'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">{formatDate(company.created_at)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400">No companies yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Partners */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white">Recent Partners</CardTitle>
              <CardDescription className="text-slate-400">Latest partner registrations</CardDescription>
            </div>
            <Link to="/admin/partners">
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-700">
                View All
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_partners?.length > 0 ? (
              <div className="space-y-4">
                {dashboard.recent_partners.map((partner) => (
                  <div 
                    key={partner.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                        <Handshake className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{partner.company_name}</p>
                        <p className="text-xs text-slate-400">{partner.email}</p>
                      </div>
                    </div>
                    <Badge 
                      className={
                        partner.status === 'active' ? 'bg-green-500' :
                        partner.status === 'pending' ? 'bg-amber-500' :
                        'bg-slate-500'
                      }
                    >
                      {partner.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Handshake className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400">No partners yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
